import { Appointment } from '../types';

/** Effective start ISO string for display/sorting: scheduledAt or flex window earliest. */
export function getAppointmentStartIso(a: Appointment | null | undefined): string | null {
  if (!a) return null;
  const iso = a.scheduledAt ?? (a as any).flexScheduledAt?.earliest ?? null;
  return typeof iso === 'string' && iso ? iso : null;
}

type AssignmentLike = {
  durationMinutes?: number;
  fromDateTime?: string;
  toDateTime?: string;
};

function parseIsoDate(value: unknown): Date | null {
  if (typeof value !== 'string' || !value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export const getAppointmentWindowDurationMinutes = (appointment: { assignments?: AssignmentLike[] } | null | undefined): number | null => {
  const assignments = (appointment as any)?.assignments;
  if (!Array.isArray(assignments) || assignments.length === 0) return null;

  const starts: Date[] = [];
  const ends: Date[] = [];

  for (const a of assignments) {
    const start = parseIsoDate((a as any)?.fromDateTime);
    const end = parseIsoDate((a as any)?.toDateTime);
    if (!start || !end) return null;
    if (end.getTime() <= start.getTime()) return null;
    starts.push(start);
    ends.push(end);
  }

  const minStartMs = Math.min(...starts.map((d) => d.getTime()));
  const maxEndMs = Math.max(...ends.map((d) => d.getTime()));
  const mins = Math.round((maxEndMs - minStartMs) / 60000);
  return Number.isFinite(mins) && mins > 0 ? mins : null;
};

export const getAppointmentSumDurationMinutes = (appointment: { assignments?: AssignmentLike[] } | null | undefined): number => {
  const assignments = (appointment as any)?.assignments;
  if (!Array.isArray(assignments) || assignments.length === 0) return 0;
  return assignments.reduce((sum: number, a: any) => sum + (Number(a?.durationMinutes) || 0), 0);
};

export const hasCustomTimeBlocks = (appointment: { assignments?: AssignmentLike[] } | null | undefined): boolean => {
  const windowMins = getAppointmentWindowDurationMinutes(appointment);
  if (windowMins == null) return false; // if we can't trust per-assignment times, treat as non-custom
  const sumMins = getAppointmentSumDurationMinutes(appointment);
  return windowMins !== sumMins;
};

// Helper function to get service names from assignments
export const getServiceNames = (appointment: Appointment): string => {
  if (!appointment || !appointment.assignments || appointment.assignments.length === 0) {
    return 'No services assigned';
  }
  return appointment.assignments.map((assignment) => assignment.serviceName || 'Unknown Service').join(', ');
};

// Helper function to get total duration for display.
// If per-assignment times are present, prefer the overall appointment window duration (min start -> max end).
// Otherwise fall back to sum of service durations.
export const getTotalDuration = (appointment: Appointment): number => {
  const windowMins = getAppointmentWindowDurationMinutes(appointment);
  if (windowMins != null) return windowMins;
  return getAppointmentSumDurationMinutes(appointment);
};

// Helper function to get total price from assignments
export const getTotalPrice = (appointment: Appointment): number => {
  if (!appointment || !appointment.assignments || appointment.assignments.length === 0) {
    return 0;
  }
  return appointment.assignments.reduce((total, assignment) => {
    // Handle both string and number price values
    let price = 0;
    if (typeof assignment.price === 'number') {
      price = assignment.price;
    } else if (typeof assignment.price === 'string') {
      price = parseFloat(assignment.price) || 0;
    }
    return total + price;
  }, 0);
};

// Helper function to get professional names from assignments
export const getProfessionalNames = (appointment: Appointment): string => {
  if (!appointment || !appointment.assignments || appointment.assignments.length === 0) {
    return '';
  }
  const professionals = appointment.assignments
    .map((assignment) => assignment.professionalFirstName && assignment.professionalLastName 
      ? `${assignment.professionalFirstName} ${assignment.professionalLastName.charAt(0)}.` 
      : null)
    .filter(Boolean);
  return Array.from(new Set(professionals)).join(', ');
};

// Helper function to get first professional initials
export const getFirstProfessionalInitials = (appointment: Appointment): string => {
  if (appointment.assignments && appointment.assignments.length > 0) {
    const firstAssignment = appointment.assignments[0];
    if (firstAssignment.professionalFirstName && firstAssignment.professionalLastName) {
      return `${firstAssignment.professionalFirstName[0]}${firstAssignment.professionalLastName[0]}`;
    }
  }
  return '';
};

// Helper function to get first professional email
export const getFirstProfessionalEmail = (appointment: Appointment): string => {
  if (appointment.assignments && appointment.assignments.length > 0) {
    return appointment.assignments[0]?.professionalEmail || 'No email available';
  }
  return 'No email available';
};

/** Positive required deposit on the appointment row that has not been fully received. */
export function isDepositOutstanding(
  appointment: {
    requiredDepositAmount?: number | string | null;
    required_deposit_amount?: number | string | null;
    receivedDepositAmount?: number | string | null;
    received_deposit_amount?: number | string | null;
    effectiveRequiredDepositAmount?: number | string | null;
    effectiveReceivedDepositAmount?: number | string | null;
    isDepositOutstanding?: boolean;
  } | null | undefined
): boolean {
  if (!appointment) return false;
  if (appointment.isDepositOutstanding === true) return true;
  if (appointment.isDepositOutstanding === false) return false;

  const effectiveRequired = appointment.effectiveRequiredDepositAmount;
  if (effectiveRequired != null) {
    const required = Number(effectiveRequired);
    if (Number.isFinite(required) && required > 0) {
      const receivedRaw = appointment.effectiveReceivedDepositAmount ?? 0;
      const received = Number(receivedRaw);
      const rec = Number.isFinite(received) ? received : 0;
      return rec < required;
    }
    return false;
  }

  const requiredRaw = appointment.requiredDepositAmount ?? appointment.required_deposit_amount;
  const required = requiredRaw != null ? Number(requiredRaw) : 0;
  if (!Number.isFinite(required) || required <= 0) return false;
  const receivedRaw = appointment.receivedDepositAmount ?? appointment.received_deposit_amount ?? 0;
  const received = Number(receivedRaw);
  const rec = Number.isFinite(received) ? received : 0;
  return rec < required;
}

/** Full deposit amount recorded when marking deposit paid (series-aware when available). */
export function getDepositAmountToRecord(
  appointment: Parameters<typeof isDepositOutstanding>[0]
): number {
  if (!appointment) return 0;
  const effectiveRequired = appointment.effectiveRequiredDepositAmount;
  if (effectiveRequired != null && Number.isFinite(Number(effectiveRequired)) && Number(effectiveRequired) > 0) {
    return Number(effectiveRequired);
  }
  const requiredRaw = appointment.requiredDepositAmount ?? appointment.required_deposit_amount;
  const required = requiredRaw != null ? Number(requiredRaw) : 0;
  if (Number.isFinite(required) && required > 0) return required;
  return getDepositAmountDue(appointment);
}

/** Amount due for an outstanding deposit (series-aware when available). */
export function getDepositAmountDue(
  appointment: Parameters<typeof isDepositOutstanding>[0]
): number {
  if (!appointment || !isDepositOutstanding(appointment)) return 0;
  const effectiveRequired = appointment.effectiveRequiredDepositAmount;
  if (effectiveRequired != null) {
    const required = Number(effectiveRequired);
    const received = Number(appointment.effectiveReceivedDepositAmount ?? 0);
    return Math.max(0, required - (Number.isFinite(received) ? received : 0));
  }
  const required = Number(appointment.requiredDepositAmount ?? appointment.required_deposit_amount ?? 0);
  const received = Number(appointment.receivedDepositAmount ?? appointment.received_deposit_amount ?? 0);
  return Math.max(0, required - (Number.isFinite(received) ? received : 0));
}

/** PENDING with a positive required deposit on the appointment row and not yet fully paid. */
export function isPendingDepositOutstanding(
  appointment: {
    status?: string | null;
    requiredDepositAmount?: number | string | null;
    required_deposit_amount?: number | string | null;
    receivedDepositAmount?: number | string | null;
    received_deposit_amount?: number | string | null;
  } | null | undefined
): boolean {
  if (!appointment || appointment.status !== 'PENDING') return false;
  const requiredRaw = appointment.requiredDepositAmount ?? appointment.required_deposit_amount;
  const required = requiredRaw != null ? Number(requiredRaw) : 0;
  if (!Number.isFinite(required) || required <= 0) return false;
  const receivedRaw = appointment.receivedDepositAmount ?? appointment.received_deposit_amount ?? 0;
  const received = Number(receivedRaw);
  const rec = Number.isFinite(received) ? received : 0;
  return rec < required;
}

export type SeriesDepositLookupEntry = {
  depositAppointmentId: string;
  requiredDepositAmount: number;
  receivedDepositAmount: number;
};

/** Map seriesId → outstanding deposit holder (first occurrence with required deposit). */
export function buildSeriesDepositLookup(
  appointments: Array<{
    id?: string | number;
    seriesId?: string | null;
    series_id?: string | null;
    requiredDepositAmount?: number | string | null;
    required_deposit_amount?: number | string | null;
    receivedDepositAmount?: number | string | null;
    received_deposit_amount?: number | string | null;
  }>
): Map<string, SeriesDepositLookupEntry> {
  const map = new Map<string, SeriesDepositLookupEntry>();
  for (const apt of appointments) {
    const seriesId = String(apt.seriesId ?? apt.series_id ?? '').trim();
    if (!seriesId || apt.id == null) continue;
    if (!isPendingDepositOutstanding(apt as any)) continue;
    const required = Number(apt.requiredDepositAmount ?? apt.required_deposit_amount ?? 0);
    const received = Number(apt.receivedDepositAmount ?? apt.received_deposit_amount ?? 0);
    map.set(seriesId, {
      depositAppointmentId: String(apt.id),
      requiredDepositAmount: required,
      receivedDepositAmount: Number.isFinite(received) ? received : 0,
    });
  }
  return map;
}

/** True when this row or its recurring series still needs a deposit payment. */
export function isPendingSeriesDepositOutstanding(
  appointment: {
    id?: string | number;
    status?: string | null;
    seriesId?: string | null;
    series_id?: string | null;
    requiredDepositAmount?: number | string | null;
    required_deposit_amount?: number | string | null;
    receivedDepositAmount?: number | string | null;
    received_deposit_amount?: number | string | null;
  } | null | undefined,
  seriesDepositLookup?: Map<string, SeriesDepositLookupEntry>
): boolean {
  if (!appointment || appointment.status !== 'PENDING') return false;
  if (isPendingDepositOutstanding(appointment)) return true;
  const seriesId = String(appointment.seriesId ?? appointment.series_id ?? '').trim();
  if (!seriesId || !seriesDepositLookup) return false;
  return seriesDepositLookup.has(seriesId);
}

/** Appointment id to use for the deposit payment route (series holder when applicable). */
export function getDepositPaymentAppointmentId(
  appointment: {
    id?: string | number;
    seriesId?: string | null;
    series_id?: string | null;
    requiredDepositAmount?: number | string | null;
    required_deposit_amount?: number | string | null;
    receivedDepositAmount?: number | string | null;
    received_deposit_amount?: number | string | null;
  },
  seriesDepositLookup?: Map<string, SeriesDepositLookupEntry>
): string {
  if (appointment.id == null) return '';
  if (isPendingDepositOutstanding(appointment)) return String(appointment.id);
  const seriesId = String(appointment.seriesId ?? appointment.series_id ?? '').trim();
  const holder = seriesId && seriesDepositLookup ? seriesDepositLookup.get(seriesId) : undefined;
  return holder?.depositAppointmentId ?? String(appointment.id);
}

/** Status label for business (and shared) appointment UI; deposit-aware for PENDING. */
export function formatAppointmentStatusLabel(
  status: string,
  appointment?: {
    status?: string | null;
    requiredDepositAmount?: number | string | null;
    required_deposit_amount?: number | string | null;
    receivedDepositAmount?: number | string | null;
    received_deposit_amount?: number | string | null;
  } | null
): string {
  if (!status) return '';
  if (status === 'PENDING_QUOTE') return 'Pending Quote';
  if (appointment && isPendingDepositOutstanding(appointment)) return 'Pending Deposit';
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
}

/** Tailwind classes for the bordered pill used on the business appointment details page. */
export function appointmentDetailsStatusBadgeClass(
  status: string,
  appointment?: {
    status?: string | null;
    requiredDepositAmount?: number | string | null;
    required_deposit_amount?: number | string | null;
    receivedDepositAmount?: number | string | null;
    received_deposit_amount?: number | string | null;
  } | null
): string {
  if (appointment && isPendingDepositOutstanding(appointment)) {
    return 'bg-orange-50 text-orange-800 border-orange-200';
  }
  switch (status) {
    case 'CONFIRMED':
      return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'PENDING':
      return 'bg-amber-50 text-amber-700 border-amber-200';
    case 'COMPLETED':
      return 'bg-green-50 text-green-700 border-green-200';
    case 'CANCELLED':
      return 'bg-red-50 text-red-700 border-red-200';
    default:
      return 'bg-gray-50 text-gray-700 border-gray-200';
  }
} 